import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import {
  alpha,
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { Fragment } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { REMOVE_PET_FROM_BOX } from "../../../../../../enums/ApiPaths";
import {
  AVAILABLE_BOXES_KEY,
  PETS_FOR_BOXES_DROP_DOWN,
} from "../../../../../../enums/QueryKeys";
import { petViewUrl } from "../../../../../../enums/UrlPaths";
import { PET_TAB_BIOMETRICS } from "../../../../../pets/enums/PetViewTabs";
import useCheckPath from "../../../../../../hooks/useCheckPath";
import useConfirm from "../../../../../../hooks/useConfirm";
import ActionRow from "../../../../../admin/clinic/components/actions/ActionRow";
import useSubmit from "../../../../../master/species/hooks/useSubmit";
import MovePetFromBox from "./MovePetFromBox";
import NotifyOwner from "./NotifyOwner";
import RemovePetFromboxConfirmation from "./RemovePetFromboxConfirmation";
import ResponsibleVet from "./ResponsibleVet";
import UnarchiveInpatientStay from "./UnarchiveInpatientStay";

const BUTTON_VARIANT = "text";
const BUTTON_SIZE = "medium";

export default function PetActions({ pet }) {
  const { id } = useParams();
  const isArchived = useCheckPath();
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const url = REMOVE_PET_FROM_BOX + pet?.id;
  const key = "single_box_key/" + id;
  const submittion = useSubmit(null, url, key, [key]);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = React.useState(false);

  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    t("AreYouSure"),
    t("Accept"),
    t("Cancel"),
    "warning",
  );

  const methods = useForm({
    defaultValues: {
      dateOfExit: dayjs(),
    },
  });

  function handleDialog() {
    setOpenDialog((prev) => !prev);
  }

  const handleRemovingPetFromBox = async () => {
    if (isArchived) return;
    getMessage(
      <FormProvider {...methods}>
        <RemovePetFromboxConfirmation />
      </FormProvider>,
    );

    const ans = await confirm();

    if (ans) {
      const payload = methods.getValues().dateOfExit;
      const res = await submittion.submitCallback(payload);
      if (res) {
        await queryClient.invalidateQueries({
          queryKey: [PETS_FOR_BOXES_DROP_DOWN],
        });
        queryClient.invalidateQueries([AVAILABLE_BOXES_KEY]);
      }
    }
  };

  return (
    <>
      <IconButton
        title={t("Actions")}
        onClick={() => handleDialog()}
        sx={{
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            transform: "scale(1.05)",
          },
        }}
      >
        <MoreVertIcon />
      </IconButton>

      <Dialog
        open={openDialog}
        onClose={() => handleDialog()}
        fullWidth={true}
        maxWidth={"xs"}
        PaperProps={{
          sx: {
            borderRadius: 8,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" component="h2" fontWeight={600}>
              {t("Actions")}
            </Typography>
          </Stack>
          <IconButton onClick={() => handleDialog()} color="error" title={t("Close")}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ opacity: 0.6 }} />

        <DialogContent sx={{ p: 0 }}>
          <ActionRow
            icon={<ThermostatIcon />}
            label={t("Biometrics")}
            description={t("ViewPetHealthData")}
            onClick={() =>
              navigate(petViewUrl(pet?.id, pet?.userId, PET_TAB_BIOMETRICS))
            }
          />
          {isArchived ? (
            <UnarchiveInpatientStay pet={pet} />
          ) : (
            <Fragment>
              <Divider />
              <NotifyOwner pet={pet} />
              <Divider />
              <MovePetFromBox pet={pet} />
              <Divider />
              <ResponsibleVet pet={pet} />
              <Divider />
              <ActionRow
                icon={<ClearIcon />}
                label={t("RemoveFromBox")}
                description={t("RecordExitDate")}
                onClick={handleRemovingPetFromBox}
                disabled={submittion.loading}
                loading={submittion.loading}
                color="error"
              />
            </Fragment>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationDialog />
    </>
  );
}
